<template>
    <div class="modal fade playbook-proposal-modal" id="playbook-proposal-modal" data-bs-backdrop="static" aria-labelledby="playbook-proposal-modal-title" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header border-bottom-0 pb-0">
                    <h3 class="mt-2 mb-2">Your Suggested Play</h3>
                    <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal" aria-label="Close" @click="closeModal">
                        <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt="">
                    </button>
                </div>
                <div class="modal-body">
                    <div v-if="loading" class="text-center py-4">
                        <div class="spinner-border text-primary" role="status"></div>
                        <p class="mt-2">Loading suggestion...</p>
                    </div>
                    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
                    <div v-else-if="suggestion" class="d-flex flex-column gap-3">
                        <div class="card segment-proposal-card">
                            <div class="card-body d-flex align-items-center gap-3">
                                <img v-if="playbookTypeImage" :src="playbookTypeImage" alt="" class="rounded" style="width: 120px; height: 80px; object-fit: cover;">
                                <div>
                                    <h5 class="mb-1">{{ suggestion.name || playbookTypeName }}</h5>
                                    <p class="mb-0 small"><span class="text-muted">Type:</span> {{ playbookTypeName }}</p>
                                    <p v-if="suggestedDateLabel" class="mb-0 small mt-1"><span class="text-muted">Campaign {{ resultDateType === 'end' ? 'end' : 'start' }}:</span> {{ suggestedDateLabel }}</p>
                                </div>
                            </div>
                        </div>
                        <div class="card segment-proposal-card">
                            <div class="card-body p-3">
                                <h6 class="mb-3">Audiences to Target</h6>
                                <div class="proposal-audiences-grid">
                                    <div
                                        v-for="(aud, i) in resultAudiences"
                                        :key="i"
                                        class="proposal-audience-card"
                                    >
                                        <div class="d-flex align-items-center gap-2 mb-2">
                                            <h4 class="proposal-audience-title">{{ displayAudienceName(aud) }}</h4>
                                            <span v-if="aud.segment_id || aud.segment" class="badge badge-gray">Existing</span>
                                            <span v-else class="badge badge-blue">New</span>
                                        </div>
                                        <div v-if="aud.segment" class="audience-info-grid">
                                            <div class="info-item">
                                                <div class="info-label">Contacts</div>
                                                <div class="info-value">{{ aud.segment.type === 'modeled' ? 'N/A' : (aud.segment.sizes && aud.segment.sizes.length > 0 ? formatNumber(aud.segment.sizes[0].size) : '0') }}</div>
                                            </div>
                                            <div class="info-item">
                                                <div class="info-label">Source</div>
                                                <div class="info-value">{{ aud.segment.data_source?.name || 'N/A' }}</div>
                                            </div>
                                        </div>
                                        <p v-else-if="aud.description" class="small text-muted mb-0 mt-1">{{ aud.description }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="hasCustomizations" class="card segment-proposal-card">
                            <div class="card-body p-3">
                                <h6 class="mb-2">Customizations</h6>
                                <div v-if="assetsToAdd.length" class="small mb-1">
                                    <strong>Assets to add{{ assetsToAdd.length === 1 ? ': ' : '' }}</strong>
                                    <template v-if="assetsToAdd.length === 1">{{ assetsToAdd[0].name || 'Asset' }}<span v-if="assetsToAdd[0].days_before_end_date != null"> ({{ assetsToAdd[0].days_before_end_date }} days before end)</span></template>
                                    <ul v-else class="mb-0 ps-3">
                                        <li v-for="(a, i) in assetsToAdd" :key="i">{{ a.name || 'Asset' }}<span v-if="a.days_before_end_date != null"> ({{ a.days_before_end_date }} days before end)</span></li>
                                    </ul>
                                </div>
                                <p v-if="assetTimingOverrides.length" class="small mb-1"><strong>Asset timing:</strong> {{ assetTimingSummary }}</p>
                                <div v-if="assetsToRemove.length" class="small mb-0">
                                    <strong>Assets to remove{{ assetsToRemove.length === 1 ? ': ' : '' }}</strong>
                                    <template v-if="assetsToRemove.length === 1">{{ assetsToRemove[0].name || assetsToRemove[0].playbook_type_asset_type_id || '—' }}</template>
                                    <ul v-else class="mb-0 ps-3">
                                        <li v-for="(a, i) in assetsToRemove" :key="i">{{ a.name || a.playbook_type_asset_type_id || '—' }}</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div v-if="resultReasoning" class="card segment-proposal-card">
                            <div class="card-body p-3">
                                <h4 class="h6 fw-semibold mb-0"><img class="icon me-2" src="/img/icons/avid-icons/edna.svg" alt="">Reasoning</h4>
                                <div class="text-muted mt-2 small" style="white-space: pre-line;">{{ resultReasoning }}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="!loading && !error && suggestion" class="modal-footer border-top-0 pt-0">
                    <button type="button" class="btn btn-secondary px-4" @click="refine"><img class="icon me-2" src="/img/icons/avid-icons/edna.svg" alt=""> Refine</button>
                    <button type="button" class="btn btn-primary px-4" :disabled="executing" @click="start">
                        <span v-if="executing" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        Start
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Swal from 'sweetalert2';
import { useClientStore } from '../../stores/client';
import { useNotifications } from '../../composables/useNotifications';

export default defineComponent({
    name: 'PlaybookProposalModal',
    props: {
        client: { type: Object, required: true },
    },
    emits: ['started', 'refine-notification-shown'],
    setup() {
        const { showNotification } = useNotifications();
        return { showNotification };
    },
    data() {
        return {
            playbookSuggestionId: null,
            suggestion: null,
            loading: false,
            error: null,
            executing: false,
        };
    },
    computed: {
        result() {
            return this.suggestion?.result || {};
        },
        resultAudiences() {
            return this.result.audiences || [];
        },
        assetsToAdd() {
            return this.result.assets_to_add || [];
        },
        assetsToRemove() {
            return this.result.assets_to_remove || [];
        },
        assetTimingOverrides() {
            return this.result.asset_timing_overrides || [];
        },
        assetTimingSummary() {
            const overrides = this.assetTimingOverrides;
            if (!overrides.length) return '';
            const days = overrides.map((o) => (o.days_before_end_date != null ? Number(o.days_before_end_date) : null)).filter((d) => d != null);
            if (!days.length) return `${overrides.length} asset${overrides.length === 1 ? '' : 's'} adjusted.`;
            const min = Math.min(...days);
            const max = Math.max(...days);
            const rangeStr = min === max ? `${min} days before campaign end` : `${min}–${max} days before campaign end`;
            return `${overrides.length} asset${overrides.length === 1 ? '' : 's'} adjusted; timing range: ${rangeStr}.`;
        },
        playbookTypeName() {
            return this.suggestion?.playbook_type?.name || this.suggestion?.playbookType?.name || 'Play';
        },
        playbookTypeImage() {
            const pt = this.suggestion?.playbook_type || this.suggestion?.playbookType;
            return pt?.thumbnail_image || null;
        },
        resultReasoning() {
            return this.result.reasoning || null;
        },
        hasCustomizations() {
            return this.assetsToAdd.length > 0 || this.assetsToRemove.length > 0 || this.assetTimingOverrides.length > 0;
        },
        resultDateType() {
            return this.result.date_type || null;
        },
        suggestedDateLabel() {
            const d = this.result.suggested_date;
            if (!d) return null;
            try {
                const date = new Date(d + 'T12:00:00');
                if (Number.isNaN(date.getTime())) return null;
                return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
            } catch {
                return d;
            }
        },
    },
    methods: {
        show(playbookSuggestionId) {
            this.playbookSuggestionId = playbookSuggestionId;
            this.suggestion = null;
            this.error = null;
            this.loading = true;
            const el = document.getElementById('playbook-proposal-modal');
            if (el && window.bootstrap) {
                let modal = window.bootstrap.Modal.getInstance(el);
                if (!modal) modal = new window.bootstrap.Modal(el);
                modal.show();
            }
            this.loadSuggestion();
        },
        closeModal() {
            const el = document.getElementById('playbook-proposal-modal');
            if (el && window.bootstrap) {
                const modal = window.bootstrap.Modal.getInstance(el);
                if (modal) modal.hide();
            }
        },
        async loadSuggestion() {
            if (!this.client?.url || !this.playbookSuggestionId) {
                this.loading = false;
                return;
            }
            try {
                const res = await fetch(`/api/playbooks/${this.client.url}/suggest/${this.playbookSuggestionId}`, {
                    headers: { Accept: 'application/json' },
                });
                const data = await res.json().catch(() => ({}));
                if (res.ok && data.playbook_suggestion) {
                    this.suggestion = data.playbook_suggestion;
                } else {
                    this.error = data.error || 'Could not load suggestion.';
                }
            } catch (e) {
                this.error = 'Could not load suggestion.';
            } finally {
                this.loading = false;
            }
        },
        displayAudienceName(aud) {
            if (aud.segment?.name) return aud.segment.name;
            if (aud.name) return aud.name;
            return aud.segment_id ? 'Existing audience' : 'New audience';
        },
        formatNumber(num) {
            if (num == null || num === '') return '0';
            const n = Number(num);
            if (Number.isNaN(n)) return '0';
            return n.toLocaleString();
        },
        refine() {
            const initialDescription = this.suggestion?.user_description || '';
            Swal.fire({
                title: 'Refine Play Suggestion',
                target: document.getElementById('playbook-proposal-modal'),
                html: `
                    <p class="mb-2">How can we improve the suggested play?</p>
                    <textarea id="playbook-refine-context" class="form-control" placeholder="E.g., Focus more on lapsed donors, or add an email two weeks before the end date." style="width: 100%; min-height: 100px;"></textarea>
                `,
                showCancelButton: true,
                confirmButtonText: 'Submit',
                cancelButtonText: 'Cancel',
                didOpen: () => {
                    const textarea = Swal.getPopup()?.querySelector('#playbook-refine-context');
                    if (textarea) {
                        textarea.focus();
                    }
                },
                preConfirm: () => {
                    const el = Swal.getPopup()?.querySelector('#playbook-refine-context');
                    const context = el?.value?.trim() ?? '';
                    if (!context) {
                        Swal.showValidationMessage('Please provide a description or feedback to refine the suggestion.');
                        return false;
                    }
                    return context;
                },
            }).then(async (result) => {
                if (!result.isConfirmed || !result.value) return;
                const feedback = result.value;
                try {
                    const res = await fetch(`/api/playbooks/${this.client.url}/suggest/${this.playbookSuggestionId}/refine`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '' },
                        body: JSON.stringify({ feedback }),
                    });
                    const data = await res.json().catch(() => ({}));
                    if (res.status === 202 && data.playbook_suggestion_id) {
                        const notificationId = this.showNotification({
                            icon: '/img/icons/nav-icons/Playbooks.svg',
                            text: 'We\'re refining the play suggestion based on your feedback. You\'ll be notified when it\'s ready!',
                            noDismiss: true,
                        });
                        this.closeModal();
                        if (notificationId) this.$emit('refine-notification-shown', notificationId);
                    } else {
                        this.showNotification({ text: data.error || 'Failed to submit refinement. Please try again.', type: 'error' });
                    }
                } catch (e) {
                    this.showNotification({ text: 'Failed to submit refinement. Please try again.', type: 'error' });
                }
            });
        },
        async start() {
            if (!this.client?.url || !this.playbookSuggestionId || !this.suggestion) return;
            const name = this.suggestion.name || this.playbookTypeName || 'My Play';
            const result = this.suggestion.result || {};
            let dateStr = result.suggested_date || null;
            let dateType = result.date_type || 'end';
            if (!dateStr) {
                const date = new Date();
                date.setDate(date.getDate() + 14);
                dateStr = date.toISOString().slice(0, 10);
            }
            this.executing = true;
            try {
                const res = await fetch(`/api/playbooks/${this.client.url}/suggest/${this.playbookSuggestionId}/execute`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '' },
                    body: JSON.stringify({ name, date: dateStr, date_type: dateType, description: this.suggestion.user_description || null }),
                });
                const data = await res.json().catch(() => ({}));
                if (res.ok && data.playbook) {
                    this.closeModal();
                    this.$emit('started', data.playbook);
                } else {
                    this.error = data.error || 'Failed to start play.';
                }
            } catch (e) {
                this.error = 'Failed to start play.';
            } finally {
                this.executing = false;
            }
        },
    },
});
</script>
